"use client"

import { queryOptions, useQuery } from "@tanstack/react-query"
import { MessageSquare } from "lucide-react"
import { useQueryStates } from "nuqs"
import { storeReviewsPageParser } from "@/components/e-commerce/store/query-params/client"
import { ReviewItem } from "@/components/shared/reviews/item"
import { ReviewsSummary } from "@/components/shared/reviews/summary"
import type {
  ReviewDisplayItem,
  ReviewsSummaryDistribution,
} from "@/components/shared/reviews/types"
import {
  buildPaginationRange,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import type { DokanStoreReview } from "@/services/dokan-service"
import { getImageWithFallback } from "@/utilities"

interface StoreReviewsProps {
  isActive: boolean
  storeId: number
}

type NormalizedStoreReview = ReviewDisplayItem

interface StoreReviewsApiResult {
  reviews: DokanStoreReview[]
  page: number
  perPage: number
  totalPages: number
  totalReviews: number
  summary: {
    average: number
    distribution: ReviewsSummaryDistribution[]
  }
}

const DEFAULT_AVATAR = "/static/img/user/default-user.jpg"
const REVIEWS_PER_PAGE = 25

export const buildStoreReviewsQueryKey = ({
  storeId,
  page,
}: {
  storeId: number
  page: number
}) => ["store-reviews", storeId, page] as const

export const getStoreReviewsQueryOptions = ({
  storeId,
  page,
}: {
  storeId: number
  page: number
}) =>
  queryOptions({
    queryKey: buildStoreReviewsQueryKey({ storeId, page }),
    staleTime: 60_000,
    placeholderData: (previousData: StoreReviewsApiResult | undefined) =>
      previousData,
    queryFn: async (): Promise<StoreReviewsApiResult> => {
      const searchParams = new URLSearchParams({
        store_id: String(storeId),
        per_page: String(REVIEWS_PER_PAGE),
        page: String(page),
      })
      const response = await fetch(
        `/api/store-reviews?${searchParams.toString()}`
      )

      if (!response.ok) {
        throw new Error("Nao foi possivel carregar os comentarios.")
      }

      return (await response.json()) as StoreReviewsApiResult
    },
  })

const normalizeStoreReviews = (
  reviews: DokanStoreReview[] = []
): NormalizedStoreReview[] =>
  reviews.map((review, index) => {
    const content = review.content?.trim() ?? ""
    const avatar = review.author?.avatar?.trim()

    return {
      id: review.id > 0 ? review.id : index + 1,
      author: review.author?.name || "Cliente",
      rating: Math.min(5, Math.max(0, Math.round(review.rating))),
      content: content.length > 0 ? content : null,
      date: review.date || new Date().toISOString(),
      verified: false,
      avatar: avatar ? getImageWithFallback(avatar) : DEFAULT_AVATAR,
    }
  })

export const StoreReviews = ({
  isActive,
  storeId,
}: StoreReviewsProps): React.ReactNode => {
  const [reviewsQuery, setReviewsQuery] = useQueryStates({
    reviews_page: storeReviewsPageParser,
  })
  const requestedPage = reviewsQuery.reviews_page

  const { data, isError, isPending } = useQuery({
    ...getStoreReviewsQueryOptions({
      page: requestedPage,
      storeId,
    }),
    enabled: Number.isInteger(storeId) && storeId > 0 && isActive,
  })

  if (isPending) {
    return (
      <div className='rounded-lg border border-muted-foreground/30 border-dashed bg-muted/30 px-6 py-12 text-center text-muted-foreground text-sm'>
        Carregando comentarios...
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className='rounded-lg border border-muted-foreground/30 border-dashed bg-muted/30 px-6 py-12 text-center text-muted-foreground text-sm'>
        Nao foi possivel carregar os comentarios da loja.
      </div>
    )
  }

  const normalizedReviews = normalizeStoreReviews(data.reviews)
  const paginationRange = buildPaginationRange(requestedPage, data.totalPages)
  const handlePageChange = (page: number): void => {
    if (page < 1 || page > data.totalPages || page === requestedPage) {
      return
    }

    setReviewsQuery({ reviews_page: page })
  }

  if (data.totalReviews === 0) {
    return (
      <div className='flex flex-col items-center justify-center gap-3 rounded-lg border border-muted-foreground/30 border-dashed bg-muted/30 px-6 py-12 text-center'>
        <MessageSquare className='h-10 w-10 text-muted-foreground/60' />
        <div>
          <h3 className='font-medium text-foreground'>
            Nenhuma avaliacao ainda
          </h3>
          <p className='mt-1 text-muted-foreground text-sm'>
            Esta loja ainda nao recebeu comentarios.
          </p>
        </div>
      </div>
    )
  }

  return (
    <section className='space-y-8'>
      <ReviewsSummary
        averageLabel='Avaliacao media'
        emptyMessage='Esta loja ainda nao possui avaliacoes.'
        starsLabel={(star) => `${star} estrelas`}
        summary={{
          average: data.summary.average,
          distribution: data.summary.distribution,
          total: data.totalReviews,
        }}
        totalLabel={(total) => `${total} avaliacoes`}
      />

      <div>
        <h3 className='mb-4 font-medium'>
          {data.totalReviews} comentarios desta loja
        </h3>

        {normalizedReviews.map((review) => (
          <ReviewItem key={review.id} review={review} />
        ))}

        {data.totalPages > 1 && (
          <div className='mt-6 flex flex-col items-center gap-2'>
            <p className='text-muted-foreground text-sm'>
              Pagina {requestedPage} de {data.totalPages}
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    aria-disabled={requestedPage <= 1}
                    className={
                      requestedPage <= 1 ? "pointer-events-none opacity-50" : ""
                    }
                    href='#'
                    onClick={(event) => {
                      event.preventDefault()
                      handlePageChange(requestedPage - 1)
                    }}
                  />
                </PaginationItem>

                {paginationRange.map((page, index) => (
                  <PaginationItem key={`${page}-${index + 1}`}>
                    {page === "ellipsis" ? (
                      <PaginationEllipsis />
                    ) : (
                      <PaginationLink
                        href='#'
                        isActive={page === requestedPage}
                        onClick={(event) => {
                          event.preventDefault()
                          handlePageChange(page)
                        }}
                      >
                        {page}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    aria-disabled={requestedPage >= data.totalPages}
                    className={
                      requestedPage >= data.totalPages
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                    href='#'
                    onClick={(event) => {
                      event.preventDefault()
                      handlePageChange(requestedPage + 1)
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </section>
  )
}
